import { stringify } from "qs";
import type { Toast } from "@/schemas/toast";

const { addToast } = useToast();

// AJAX response type
export type AjaxResponse<T> = | { success: false; data: { message: string; toasts: Toast[] } } | { success: true; data: T & { toasts: Toast[] } };

// Allowed value types in params
type AjaxPrimitive = string | number | boolean | Date;
type AjaxValue = AjaxPrimitive | AjaxValue[] | { [key: string]: AjaxValue };
export type AjaxParams = Record<string, AjaxValue>;

// Helper: Recursively converts Date objects to ISO strings within nested data
function normalizeParams<T extends AjaxValue>(value: T): T {
  if (value instanceof Date) return value.toISOString() as T;
  if (Array.isArray(value)) return value.map((v) => normalizeParams(v)) as T;
  if (typeof value === "object" && value !== null) {
    const result: Record<string, AjaxValue> = {};
    for (const [key, val] of Object.entries(value)) {
      result[key] = normalizeParams(val);
    }
    return result as T;
  }
  return value;
}

// Helper: Perform AJAX request
export async function ajaxRequest<T>(action: string, params: AjaxParams = {}): Promise<T> {
  const headers = { "Content-Type": "application/x-www-form-urlencoded" };
  const normalized = normalizeParams({ action, ...params });
  const body = stringify(normalized);
  const response = await fetch("/wp-admin/admin-ajax.php", { method: "POST", headers, body });
  if (!response.ok) throw new Error("La requête a échoué");
  const result = await response.json() as AjaxResponse<T>;
  if (!result.success) {
    if (result.data.toasts) {
      result.data.toasts.forEach(addToast);
    }
    throw new Error(result.data.message);
  }
  if (result.data.toasts) {
    result.data.toasts.forEach(addToast);
  }
  return result.data;
}
